import React, { useState, useEffect } from 'react';
import {
  FiGift,
  FiClock,
  FiCheckCircle,
  FiCalendar,
  FiStar
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { sendApiRequest } from '../utils.js';
import { motion, AnimatePresence } from 'framer-motion';

const MyRewards = () => {
  const userData = useSelector(state => state.user_info[0]?.user);
  const [rewards, setRewards] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('qualified');
  const [qualifiedData, setQualifiedData] = useState({
    totalQualifiedPackages: 0,
    selfQualifiedCount: 0,
    teamQualifiedCount: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rewardsRes, receiptsRes, qualifiedCountRes] = await Promise.all([
          sendApiRequest(`api/rewards`, {}, "GET"),
          sendApiRequest(`api/packages/all_manual/receipts?partnerEmail=${userData?.email}`, {}, "GET"),
          sendApiRequest(`api/packages/qualified-packages-count/${userData?._id}`, {}, "GET")
        ]);
        setRewards(rewardsRes.data || []);
        setReceipts(receiptsRes.data.data.receipts || []);
        if (qualifiedCountRes.data.success) {
          setQualifiedData(qualifiedCountRes.data.data);
        }
      } catch (err) {
        console.error(err);
        setRewards([]);
        setReceipts([]);
      } finally {
        setLoading(false);
      }
    };

    if (userData?.email && userData?._id) {
      fetchData();
    }
  }, [userData?.email, userData?._id]);

  // Total business receipts count (self + team)
  const businessReceiptsCount = qualifiedData.totalQualifiedPackages;

  const qualifiedRewards = rewards.filter(
    reward => reward.status === 'active' && reward.requiredBookings <= businessReceiptsCount
  );

  const upcomingRewards = rewards.filter(
    reward => reward.status === 'active' && reward.requiredBookings > businessReceiptsCount
  );

  const formatAmount = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getRewardTypeIcon = (type) => {
    switch (type) {
      case 'trip':
        return '✈️';
      case 'vehicle':
        return '🚗';
      case 'cash':
        return '💰';
      default:
        return '🎁';
    }
  };

  const getProgressPercent = (required) => {
    return Math.min((businessReceiptsCount / required) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FiGift className="w-6 h-6 text-indigo-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-500 font-medium">Loading your rewards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-indigo-50/30 to-purple-50/30 py-6 sm:py-10">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-purple-500 rounded-full"></div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  My Rewards
                </h1>
              </div>
              <p className="text-gray-500 mt-1 text-sm">
                Track your rewards progress and unlock amazing perks!
              </p>
            </div>
          </div>
        </div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500 mb-2">My Bookings</p>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
                  <FiCheckCircle className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-indigo-600">{qualifiedData.selfQualifiedCount}</p>
                  <p className="text-xs text-gray-400">Completed</p>
                </div>
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500 mb-2">Team Bookings</p>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center">
                  <FiCheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-blue-600">{qualifiedData.teamQualifiedCount}</p>
                  <p className="text-xs text-gray-400">Completed</p>
                </div>
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500 mb-2">Qualified Rewards</p>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
                  <FiStar className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-emerald-600">{qualifiedRewards.length}</p>
                  <p className="text-xs text-gray-400">Unlocked</p>
                </div>
              </div>
            </div>

            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500 mb-2">Next Reward</p>
              <div className="flex items-center justify-center md:justify-start gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <FiStar className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-600">
                    {upcomingRewards.length > 0 ? upcomingRewards[0].requiredBookings - businessReceiptsCount : 0}
                  </p>
                  <p className="text-xs text-gray-400">Bookings Away</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex gap-2">
              {[
                { id: 'qualified', label: 'Qualified Rewards', count: qualifiedRewards.length, color: 'emerald' },
                { id: 'upcoming', label: 'Upcoming Rewards', count: upcomingRewards.length, color: 'amber' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            <AnimatePresence mode="wait">
              {activeTab === 'qualified' && qualifiedRewards.length > 0 ? (
                <motion.div
                  key="qualified"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {qualifiedRewards.map((reward, index) => (
                    <motion.div
                      key={reward._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-200 shadow-lg"
                    >
                      <div className="relative">
                        {reward.image && (
                          <img
                            src={reward.image}
                            alt={reward.title}
                            className="w-full h-40 object-cover rounded-xl mb-4"
                          />
                        )}
                        {!reward.image && (
                          <div className="w-full h-40 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4">
                            <span className="text-6xl">{getRewardTypeIcon(reward.rewardType)}</span>
                          </div>
                        )}
                        <div className="absolute -top-2 -right-2 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                          UNLOCKED
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-800 mb-2">{reward.title}</h3>
                      {reward.description && (
                        <p className="text-sm text-gray-600 mb-4">{reward.description}</p>
                      )}

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                          <span>{reward.requiredBookings} Bookings Required (Completed)</span>
                        </div>
                        {reward.rewardValue > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiGift className="w-4 h-4 text-emerald-500" />
                            <span>Value: {formatAmount(reward.rewardValue)}</span>
                          </div>
                        )}
                        {reward.startDate && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiCalendar className="w-4 h-4 text-emerald-500" />
                            <span>Started: {new Date(reward.startDate).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                        {reward.endDate && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiCalendar className="w-4 h-4 text-emerald-500" />
                            <span>Ends: {new Date(reward.endDate).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : activeTab === 'qualified' && qualifiedRewards.length === 0 ? (
                <motion.div
                  key="no-qualified"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiGift className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Qualified Rewards Yet</h3>
                  <p className="text-gray-500">Keep booking packages to unlock amazing rewards!</p>
                </motion.div>
              ) : activeTab === 'upcoming' && upcomingRewards.length > 0 ? (
                <motion.div
                  key="upcoming"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {upcomingRewards.map((reward, index) => (
                    <motion.div
                      key={reward._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg"
                    >
                      <div className="relative">
                        {reward.image && (
                          <img
                            src={reward.image}
                            alt={reward.title}
                            className="w-full h-40 object-cover rounded-xl mb-4"
                          />
                        )}
                        {!reward.image && (
                          <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-4">
                            <span className="text-6xl">{getRewardTypeIcon(reward.rewardType)}</span>
                          </div>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-gray-800 mb-2">{reward.title}</h3>
                      {reward.description && (
                        <p className="text-sm text-gray-600 mb-4">{reward.description}</p>
                      )}

                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600 font-medium">Progress</span>
                          <span className="text-indigo-600 font-bold">
                            {businessReceiptsCount}/{reward.requiredBookings}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${getProgressPercent(reward.requiredBookings)}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-full rounded-full"
                          ></motion.div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {reward.requiredBookings - businessReceiptsCount} more bookings needed!
                        </p>
                      </div>

                      <div className="space-y-2">
                        {reward.rewardValue > 0 && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiGift className="w-4 h-4 text-indigo-500" />
                            <span>Value: {formatAmount(reward.rewardValue)}</span>
                          </div>
                        )}
                        {reward.startDate && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiCalendar className="w-4 h-4 text-indigo-500" />
                            <span>Started: {new Date(reward.startDate).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                        {reward.endDate && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FiCalendar className="w-4 h-4 text-indigo-500" />
                            <span>Ends: {new Date(reward.endDate).toLocaleDateString('en-IN')}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="no-upcoming"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <FiStar className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No Upcoming Rewards</h3>
                  <p className="text-gray-500">Check back soon for new reward schemes!</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Summary Note */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FiCheckCircle className="w-4 h-4 text-amber-500" />
            Important Note
          </h4>
          <p className="text-sm text-gray-600">
            To qualify for a reward, each booked package must have at least <strong>5 EMI payments</strong> completed. 
            Bookings from both you and your entire team count towards your reward requirements!
            Once you qualify, please contact the admin to claim your reward!
          </p>
        </div>
      </div>
    </div>
  );
};

export default MyRewards;
